import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {DataObjectId, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tag} from '@components/common/base/tag'
import {useRightPanelContentStore} from '@components/right-panel/right-panel-content/right-panel-content-store'
import {useSyncEvent} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {cn} from '../../../containers/html-utils'

type QuickLinks = ReadonlyArray<[string, number]>

const getQuickLinks = (
  companyId: DataObjectId,
  chatId: DataObjectId
): QuickLinks => {
  const hub = Sync.get({companyId})?.get(chatId) as Hub
  return hub
    ? hub.chat
        .getLabels()
        .map(ql => [ql.label, ql.timestamp] as [string, number])
    : []
}

const useQuickLinks = (
  companyId: DataObjectId,
  chatId: DataObjectId
): QuickLinks => {
  const [quickLinks, setQuickLinks] = useState<QuickLinks>([])
  useEffect(() => {
    setQuickLinks(getQuickLinks(companyId, chatId))
  }, [setQuickLinks, companyId, chatId])
  useSyncEvent(
    'custom',
    {filter: chatId, companyId},
    () => setQuickLinks(getQuickLinks(companyId, chatId)),
    [setQuickLinks, chatId, companyId]
  )
  return quickLinks
}

const TwQuickLinks = tw.div`
  max-h-[76px]
  overflow-y-auto
  flex
  flex-wrap
  items-center
  gap-8
`

interface Props {
  companyId: DataObjectId
  chatId: DataObjectId
  className?: string
  paddings?: string
  containerSelector?: string
}

export const TaskChatQuickLinkList = (props: Props) => {
  const {
    companyId,
    chatId,
    className,
    paddings = 'my-12 pl-24 pr-18 mr-12',
    containerSelector
  } = props
  const [, panelStore] = useRightPanelContentStore()
  const quickLinks = useQuickLinks(companyId, chatId)
  const refQuickLinksCount = useRef(0)

  useEffect(() => {
    if (quickLinks.length !== refQuickLinksCount.current) {
      refQuickLinksCount.current = quickLinks.length
      panelStore.updateChatPin()
    }
  }, [quickLinks.length])

  return quickLinks.length ? (
    <TwQuickLinks className={cn(paddings, className)}>
      {quickLinks.map(x => (
        <Tag
          key={x[1]}
          icon="IconPinned"
          onClick={() => ChatUtils.goToLabel(x[1] + '', containerSelector)}
        >
          {x[0]}
        </Tag>
      ))}
    </TwQuickLinks>
  ) : null
}
